// Libs
import React, { useEffect, useMemo, useRef, useState } from 'react';
// Utils
import GENERAL from 'utils/constants/general';
import { debounce, filterOption } from 'utils/libs';
import DataFieldManagerUtils from 'components/DataFieldManager/DataFieldManagerUtils';
import { getValueFromRowData } from 'components/DataFieldManager/OnlyReadField/OnlyReadField';
import OrderAuthorization from 'modules/Orders/Authorization';
// Hooks
import { useOrderUserAssignment, useTheme } from 'hooks';
import {
	getUniqueUserAssignments,
	getUserAssignmentCompatibilityMessage,
	getUserAssignmentIdentity,
} from 'hooks/Orders/useOrderAssignments.utils';
import { useSelector } from 'react-redux';
// Components
import { Wrapper, LogoIcon, Select, Span } from 'components';
import Updating from '../Updating';
import Status from '../Status';
// Selectors
import { selectProfile } from 'screens/Login/selectors';
// Styles
import styles from './AssignedTechId.module.css';

const { SERVICE_ID, ZONE_ID, CENTRAL_ID, ENTITY_ID, ENTITY_NAME } =
	DataFieldManagerUtils.getFieldIds();
const { UNSUCCESS } = GENERAL.ENV.STATUS;
const componentStyle = {
	select: {
		dark: styles.containerSelectDark,
		light: styles.containerSelectLight,
		disabled: styles.containerSelectDisabled,
	},
	dropdown: {
		dark: styles.containerDropdownDark,
		light: styles.containerDropdownLight,
		disabled: styles.containerDropdownDisabled,
	},
};

const getUsersWithCurrentUser = (currentUser, users) => {
	const uniqueUsers = getUniqueUserAssignments(users);
	const currentUserIdentity = getUserAssignmentIdentity(currentUser);
	const hasCurrentUser = uniqueUsers.some(
		user => getUserAssignmentIdentity(user) === currentUserIdentity,
	);
	const hasCurrentUserId =
		currentUser.id !== undefined && currentUser.id !== null;
	const hasCurrentUserEntityId =
		currentUser.entityId !== undefined && currentUser.entityId !== null;
	const hasUserWithCurrentUserId = uniqueUsers.some(
		user =>
			getUserAssignmentIdentity({ id: user.id }) ===
			getUserAssignmentIdentity({ id: currentUser.id }),
	);
	const shouldAddCurrentUser =
		!hasCurrentUser &&
		hasCurrentUserId &&
		(hasCurrentUserEntityId || !hasUserWithCurrentUserId);
	return shouldAddCurrentUser ? [currentUser, ...uniqueUsers] : uniqueUsers;
};

const Selection = ({
	orderUserAssignment,
	rowData,
	selected,
	field,
	style,
	isAuthorized,
	options,
	disabled = false,
	selectedOptionChallenge,
	placeholder,
	disabledReason,
}) => {
	const { themeProfile } = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const { state, onGetUserAssignment, onSetUserAssignment, onRetry } =
		orderUserAssignment;
	const onGetUserAssignmentRef = useRef(onGetUserAssignment);
	onGetUserAssignmentRef.current = onGetUserAssignment;
	const fetchDebouncedUserAssignment = useMemo(
		() => debounce(params => onGetUserAssignmentRef.current(params), 200),
		[],
	);
	useEffect(
		() => () => {
			fetchDebouncedUserAssignment.cancel?.();
		},
		[fetchDebouncedUserAssignment],
	);

	const hasStaticOptions = Array.isArray(options);
	if (!hasStaticOptions && state.status === UNSUCCESS)
		return (
			<Status
				className={componentStyle[style][themeProfile]}
				message={state.message}
				onRetry={onRetry}
			/>
		);

	if (!hasStaticOptions && state.isSetting)
		return <Updating className={componentStyle[style][themeProfile]} />;

	const onChange = field.onChange || onSetUserAssignment;

	const rows = Array.isArray(rowData) ? rowData : [rowData];
	const isBulk = rows.length > 1;
	const firstRow = rows[0];
	const filterData = {
		orders: rows.map(row => ({
			serviceId: row[SERVICE_ID],
			zoneId: row.orderZone?.zoneId || row.contractZone?.zoneId || row[ZONE_ID],
			centralId: row.central?.id || row[CENTRAL_ID],
		})),
		serviceId: firstRow[SERVICE_ID],
		zoneId:
			firstRow.orderZone?.zoneId ||
			firstRow.contractZone?.zoneId ||
			firstRow[ZONE_ID],
		centralId: firstRow.central?.id || firstRow[CENTRAL_ID],
	};

	const currentUserValue = isBulk
		? undefined
		: getValueFromRowData({ field, rowData: firstRow });
	const currentUser = {
		id: isBulk ? undefined : firstRow[field.id],
		name: isBulk
			? undefined
			: (field.uiValueProp && currentUserValue) ||
				firstRow.assignedTechName ||
				field.uiValue ||
				currentUserValue,
		workCode: isBulk ? undefined : firstRow.assignedTechWorkCode,
		entityId: isBulk ? undefined : firstRow[ENTITY_ID],
		entityName: isBulk ? undefined : firstRow[ENTITY_NAME],
	};
	const dynamicUsers = getUniqueUserAssignments(state.data);
	const _users = hasStaticOptions
		? options
		: !isOpen
			? dynamicUsers
			: getUsersWithCurrentUser(currentUser, dynamicUsers);
	const getOptionLabel = user => {
		const identity =
			[user.name, user.workCode].filter(Boolean).join(' · ') ||
			`Técnico #${user.id}`;
		const entity =
			user.entityName ||
			(user.entityId === undefined || user.entityId === null
				? undefined
				: `Entidad #${user.entityId}`);
		return [identity, entity].filter(Boolean).join(' · ');
	};
	const selectedDynamicUser =
		state.selectedAssignedTechId !== undefined &&
		state.selectedAssignedTechId !== null
			? {
					id: state.selectedAssignedTechId,
					entityId: state.selectedAssignedTechEntityId,
				}
			: currentUser;
	const selectedDynamicIdentity =
		getUserAssignmentIdentity(selectedDynamicUser);
	const hasSelectedDynamicOption = _users.some(
		user => getUserAssignmentIdentity(user) === selectedDynamicIdentity,
	);
	const selectedDynamicValue =
		selectedDynamicUser.id === undefined ||
		selectedDynamicUser.id === null ||
		!hasSelectedDynamicOption
			? undefined
			: selectedDynamicIdentity;

	const getClassName = () => {
		if (disabled || !isAuthorized || disabledReason)
			return componentStyle[style].disabled;
		return componentStyle[style][themeProfile];
	};
	return (
		<div
			onClick={e => e.stopPropagation()}
			className={getClassName()}
			title={disabledReason}
		>
			<Select
				width='100%'
				showSearch
				filterOption={filterOption}
				value={
					!_users.length || isBulk
						? undefined
						: hasStaticOptions
							? selectedOptionChallenge
							: selectedDynamicValue
				}
				placeholder={placeholder || currentUser.name}
				onSearch={
					hasStaticOptions
						? undefined
						: searchValue =>
								fetchDebouncedUserAssignment({
									...filterData,
									searchValue,
								})
				}
				onChange={(_value, { props }) => {
					const assignedTechId = props.assignedTechId;
					if (
						!isBulk &&
						!hasStaticOptions &&
						_value === getUserAssignmentIdentity(currentUser)
					)
						return;
					onChange({
						field,
						rowData,
						selected,
						option: {
							id: assignedTechId,
							name: props.candidateName || props.children,
							entityId: props.entityId,
							entityName: props.entityName,
							workCode: props.workCode,
							divisionId: props.divisionId,
							selectionChallenge: props.selectionChallenge,
						},
					});
				}}
				onDropdownVisibleChange={visible => {
					if (visible && !hasStaticOptions) onGetUserAssignment(filterData);
					if (!visible) fetchDebouncedUserAssignment.cancel?.();
					setIsOpen(visible);
				}}
				dropdownRender={menu => (
					<>
						{menu}
						{!hasStaticOptions && state.isLoading && (
							<Wrapper width='100%' justifyContent='center'>
								<LogoIcon spin={true} />
							</Wrapper>
						)}
					</>
				)}
				disabled={disabled || !isAuthorized || !!disabledReason}
			>
				{_users.map(user => (
					<Select.Option
						key={
							hasStaticOptions
								? user.selectionChallenge
								: getUserAssignmentIdentity(user)
						}
						value={
							hasStaticOptions
								? user.selectionChallenge
								: getUserAssignmentIdentity(user)
						}
						assignedTechId={user.id}
						entityId={user.entityId}
						entityName={user.entityName}
						workCode={user.workCode}
						divisionId={user.divisionId}
						selectionChallenge={user.selectionChallenge}
						candidateName={user.name}
					>
						{getOptionLabel(user)}
					</Select.Option>
				))}
			</Select>
		</div>
	);
};

const AssignedTechId = ({
	field,
	rowData,
	selected,
	style = 'select',
	options,
	authorized,
	disabled = false,
	selectedOptionChallenge,
	placeholder,
}) => {
	const profile = useSelector(selectProfile);
	const orderUserAssignment = useOrderUserAssignment();
	const { isDarkTheme } = useTheme();
	const { onRetry } = orderUserAssignment;
	const hasStaticOptions = Array.isArray(options);
	const isAuthorized = hasStaticOptions
		? authorized === true
		: new OrderAuthorization(profile).checkEditOrderUser();
	const disabledReason = Array.isArray(rowData)
		? getUserAssignmentCompatibilityMessage(rowData)
		: undefined;

	if (!hasStaticOptions && orderUserAssignment.state.status === UNSUCCESS)
		return (
			<Status
				className={isDarkTheme ? styles.containerDark : styles.containerLight}
				message={orderUserAssignment.state.message}
				onRetry={onRetry}
			/>
		);

	if (!hasStaticOptions && orderUserAssignment.state.isSetting)
		return (
			<Updating
				className={isDarkTheme ? styles.containerDark : styles.containerLight}
			/>
		);

	return style === 'dropdown' ? (
		<Selection
			orderUserAssignment={orderUserAssignment}
			rowData={rowData}
			selected={selected}
			field={field}
			style={style}
			isAuthorized={isAuthorized}
			options={options}
			disabled={disabled}
			selectedOptionChallenge={selectedOptionChallenge}
			placeholder={placeholder}
			disabledReason={disabledReason}
		/>
	) : style === 'select' && !isAuthorized ? (
		<Span>{rowData.assignedTechName}</Span>
	) : (
		<Selection
			orderUserAssignment={orderUserAssignment}
			rowData={rowData}
			selected={selected}
			field={field}
			style={style}
			isAuthorized={isAuthorized}
			options={options}
			disabled={disabled}
			selectedOptionChallenge={selectedOptionChallenge}
			placeholder={placeholder}
			disabledReason={disabledReason}
		/>
	);
};

export default AssignedTechId;
